import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type {
  AuctionsOfAuctionTileParams,
  ParticipatingUpaAuctionsOfAuctionTileParams,
} from 'src/apis/transport-auction/types';
import { useAuctionTile } from 'src/apis/transport-auction/use-auction-tile';
import { useAuctionsOfAuctionTile } from 'src/apis/transport-auction/use-auctions-of-auction-tile';
import { useParticipatingUpaAuctionsOfAuctionTile } from 'src/apis/transport-auction/use-participating-upa-auctions-of-auction-tile';
import {
  useTransportAuctionServiceRoot,
  type OptionalTransportAuctionServiceRootRequest,
} from 'src/apis/transport-auction/use-transport-auction-service-root';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useAuctionsPageParams } from 'src/pages/transport/auctions/tiles/use-page-params';
import { isConstraintViolation } from 'src/utils/is-constraint-violation';

const Page = lazy(() => import('./page'));
const ErrorPage = lazy(() => import('./error'));

export const RunningAuctionsOverviewTAS: FC = () => {
  const transportAuctionServiceRoot = useTransportAuctionServiceRoot();

  if (!transportAuctionServiceRoot.response) return <PageSpinner />;

  return <LoadData transportAuctionServiceRoot={transportAuctionServiceRoot} />;
};

const LoadData: FC<{
  transportAuctionServiceRoot: Successful<OptionalTransportAuctionServiceRootRequest>;
}> = ({ transportAuctionServiceRoot }) => {
  const { tileId } = useParams<{ tileId: string }>();
  const pageParams = useAuctionsPageParams();

  const tile = useAuctionTile({
    root: transportAuctionServiceRoot,
    tileId,
  });

  const auctionsParams = useMemo(
    () =>
      ({
        limit: pageParams.auctions.value.pageSize,
        offset: pageParams.auctions.value.start,
        sortBy: pageParams.auctions.value.sortBy,
        sortDirection: pageParams.auctions.value.sortDirection,
        quickFilterId: pageParams.auctions.value.quickFilterId,
        favouritesOnly:
          pageParams.auctions.value.quickFilter === 'FAVOURITES_ONLY'
            ? true
            : pageParams.auctions.value.quickFilter === 'DEFAULT'
              ? null
              : false,
        ownAuctionsOnly:
          pageParams.auctions.value.quickFilter === 'OWN_AUCTIONS_ONLY'
            ? true
            : pageParams.auctions.value.quickFilter === 'DEFAULT'
              ? null
              : false,
        networkPointOrBundleId:
          pageParams.auctions.value.networkPointOrBundleId.map(
            (option) => option.value
          ),
        direction: pageParams.auctions.value.direction,
        operatorId: pageParams.auctions.value.operatorId.map(
          (option) => option.value
        ),
        capacityCategoryId: pageParams.auctions.value.capacityCategoryId.map(
          (option) => option.value
        ),
        marketableCapacityMin: pageParams.auctions.value.marketableCapacityMin,
        marketableCapacityMax: pageParams.auctions.value.marketableCapacityMax,
        runtimeIntersectionFilterStart:
          pageParams.auctions.value.runtimeIntersectionFilterStart,
        runtimeIntersectionFilterEnd:
          pageParams.auctions.value.runtimeIntersectionFilterEnd,
        startingPriceMin: pageParams.auctions.value.startingPriceMin,
        startingPriceMax: pageParams.auctions.value.startingPriceMax,
        marketTransitionPriceMin:
          pageParams.auctions.value.marketTransitionPriceMin,
        marketTransitionPriceMax:
          pageParams.auctions.value.marketTransitionPriceMax,
        assignedAuctionId: pageParams.auctions.value.assignedAuctionId,
      }) satisfies AuctionsOfAuctionTileParams,
    [pageParams.auctions.value]
  );
  const auctions = useAuctionsOfAuctionTile({
    root: transportAuctionServiceRoot,
    tileId,
    params: auctionsParams,
  });

  const participatingUpaAuctionsParams = useMemo(
    () =>
      ({
        limit: pageParams.participatingUpaAuctions.value.pageSize,
        offset: pageParams.participatingUpaAuctions.value.start,
      }) satisfies ParticipatingUpaAuctionsOfAuctionTileParams,
    [pageParams.participatingUpaAuctions.value]
  );
  const participatingUpaAuctions = useParticipatingUpaAuctionsOfAuctionTile({
    root: transportAuctionServiceRoot,
    tileId,
    params: participatingUpaAuctionsParams,
  });

  if (tile.error) return <ErrorPage error={tile.error} />;
  if (auctions.error && !isConstraintViolation(auctions.error))
    return <ErrorPage error={auctions.error} />;
  if (participatingUpaAuctions?.error)
    return <ErrorPage error={participatingUpaAuctions.error} />;

  if (
    !tile.response ||
    !auctions.loaded ||
    participatingUpaAuctions?.response === null
  )
    return <PageSpinner />;

  return (
    <Page
      transportAuctionServiceRoot={transportAuctionServiceRoot}
      tile={tile}
      auctions={auctions}
      participatingUpaAuctions={participatingUpaAuctions}
      pageParams={pageParams}
    />
  );
};
